<script setup lang="ts">
import { ref } from 'vue'
import { Copy, User, Building2, ChevronDown, ChevronRight, Wallet, CreditCard, Building, AlertCircle, Pencil, MoreHorizontal } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import type { CombinedCustomer, VirtualAccount, ExternalAccount, BridgeWallet } from '@/types/bridge'
import { API, HOST } from '@/utils/http'
import { useToast } from '@/components/ui/toast/use-toast'
import ConnectExternalAccountForm from './ConnectExternalAccountForm.vue'
import CreateVirtualAccountForm from './CreateVirtualAccountForm.vue'
import KycStatusDisplay from '@/components/payments/KycStatusDisplay.vue'
import ChangeContactEmailDialog from './ChangeContactEmailDialog.vue'

const props = defineProps<{
  customers: CombinedCustomer[]
  loading: boolean
}>()

// Track expanded rows
const expandedRows = ref<Set<string>>(new Set())

// Track which customer is showing the connect form
const showingConnectForm = ref<string | null>(null)

// Track which customer is showing the create virtual account form
const showingCreateVA = ref<string | null>(null)

const handleVASuccess = async (customer: CombinedCustomer) => {
  showingCreateVA.value = null
  await fetchCustomerAccounts(customer)
}

// Track account data for each customer
interface CustomerAccountData {
  virtualAccounts: VirtualAccount[] | null
  externalAccounts: ExternalAccount[] | null
  wallets: BridgeWallet[] | null
  ledgerBalance: string | null
  bridgeProfile: Record<string, any> | null
  loading: boolean
  error: string | null
}

const customerAccountsData = ref<Map<string, CustomerAccountData>>(new Map())

const toggleRow = async (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`
  if (expandedRows.value.has(key)) {
    expandedRows.value.delete(key)
  } else {
    expandedRows.value.add(key)
    // Fetch account data if not already loaded
    if (!customerAccountsData.value.has(key)) {
      await fetchCustomerAccounts(customer)
    }
  }
}

const isExpanded = (customer: CombinedCustomer) => {
  return expandedRows.value.has(`${customer.type}-${customer.id}`)
}

const fetchCustomerAccounts = async (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`

  // Initialize loading state
  customerAccountsData.value.set(key, {
    virtualAccounts: null,
    externalAccounts: null,
    wallets: null,
    ledgerBalance: null,
    bridgeProfile: null,
    loading: true,
    error: null
  })

  try {
    // Fetch all account types in parallel (+ bridge profile for business customers)
    const [virtualAccountsRes, externalAccountsRes, walletsRes, ledgerBalanceRes, bridgeProfileRes] = await Promise.all([
      API.get(`${HOST}/payments/bridge/customers/${customer.id}/virtual-accounts`).catch(() => ({ data: [] })),
      API.get(`${HOST}/payments/bridge/customers/${customer.id}/external-accounts`).catch(() => ({ data: [] })),
      API.get(`${HOST}/payments/bridge/customers/${customer.id}/wallets`).catch(() => ({ data: { wallets: [] } })),
      customer.type === 'business'
        ? API.get(`${HOST}/payments/bridge/customers/${customer.id}/ledger-balance`).catch(() => ({ data: { balance: '0.00', currency: 'usdb' } }))
        : Promise.resolve({ data: null }),
      customer.type === 'business' && customer.bridge_customer_id
        ? API.get(`${HOST}/payments/bridge/customers/${customer.id}/bridge-profile`).catch(() => ({ data: null }))
        : Promise.resolve({ data: null })
    ])

    // Filter out null/undefined items and ensure we have arrays
    const virtualAccounts = Array.isArray(virtualAccountsRes.data)
      ? virtualAccountsRes.data.filter(item => item && item.id)
      : []
    const externalAccounts = Array.isArray(externalAccountsRes.data)
      ? externalAccountsRes.data.filter(item => item && item.id)
      : []
    const wallets = Array.isArray(walletsRes.data?.wallets)
      ? walletsRes.data.wallets.filter((item: any) => item && item.id)
      : []

    customerAccountsData.value.set(key, {
      virtualAccounts,
      externalAccounts,
      wallets,
      ledgerBalance: ledgerBalanceRes.data ? `${ledgerBalanceRes.data.balance} ${ledgerBalanceRes.data.currency?.toUpperCase()}` : null,
      bridgeProfile: bridgeProfileRes.data || null,
      loading: false,
      error: null
    })
  } catch (error: any) {
    customerAccountsData.value.set(key, {
      virtualAccounts: null,
      externalAccounts: null,
      wallets: null,
      ledgerBalance: null,
      bridgeProfile: null,
      loading: false,
      error: error.response?.data?.detail || 'Failed to load account data'
    })
  }
}

const getAccountData = (customer: CombinedCustomer): CustomerAccountData | null => {
  const key = `${customer.type}-${customer.id}`
  return customerAccountsData.value.get(key) || null
}

const showConnectForm = (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`
  showingConnectForm.value = key
}

const hideConnectForm = () => {
  showingConnectForm.value = null
}

const isShowingConnectForm = (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`
  return showingConnectForm.value === key
}

const handleConnectSuccess = async (customer: CombinedCustomer) => {
  // Hide the form
  hideConnectForm()

  // Refresh the account data for this customer
  await fetchCustomerAccounts(customer)
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
}

const emit = defineEmits<{
  (e: 'customer-updated'): void
}>()

const { toast } = useToast()

const confirmUnenrollDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingUnenroll = ref<CombinedCustomer | null>(null)
const unenrolling = ref(false)

const requestUnenroll = (customer: CombinedCustomer) => {
  pendingUnenroll.value = customer
  confirmUnenrollDialog.value?.open()
}

const confirmUnenroll = async () => {
  const customer = pendingUnenroll.value
  if (!customer) return
  unenrolling.value = true
  try {
    await API.delete(`${HOST}/payments/bridge/customers/${customer.id}`)
    toast({ title: 'Customer unenrolled' })
    emit('customer-updated')
  } catch (err: any) {
    const msg = err.response?.data?.detail || 'Failed to unenroll customer'
    toast({ title: 'Unenroll failed', description: msg, variant: 'destructive' })
  } finally {
    unenrolling.value = false
    pendingUnenroll.value = null
  }
}

const changeEmailTarget = ref<CombinedCustomer | null>(null)

const handleEmailUpdated = (_customer: CombinedCustomer, _newEmail: string) => {
  changeEmailTarget.value = null
  emit('customer-updated')
}

const getOverallStatus = (customer: CombinedCustomer): 'approved' | 'rejected' | 'awaiting_ubo' | 'in_progress' => {
  const tosApproved = customer.tos_status === 'approved'
  const kycApproved = customer.kyc_status === 'approved'
  const kycRejected = ['rejected', 'paused', 'offboarded'].includes(customer.kyc_status)
  const awaitingUbo = customer.kyc_status === 'awaiting_ubo'

  if (tosApproved && kycApproved) return 'approved'
  if (kycRejected) return 'rejected'
  if (awaitingUbo) return 'awaiting_ubo'
  return 'in_progress'
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<template>
  <div class="h-full">
    <!-- Loading state -->
    <div v-if="loading" class="p-4">
      <div class="space-y-3">
        <div v-for="i in 5" :key="i" class="flex items-center space-x-3 animate-pulse">
          <div class="h-10 w-10 bg-muted rounded-full"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-muted rounded w-1/4"></div>
            <div class="h-4 bg-muted rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="customers.length === 0" class="border rounded-md p-8 text-center text-muted-foreground">
      No Bridge customers yet. Create one using the form above.
    </div>

    <!-- Combined table -->
    <div v-else class="border rounded-md">
      <table class="w-full">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="h-12 w-10 px-2 text-left align-middle font-medium text-muted-foreground"></th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Type</th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
            <th class="h-12 px-4 text-center align-middle font-medium text-muted-foreground">Status</th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Created</th>
            <th class="h-12 w-10 px-2"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="customer in customers" :key="`${customer.type}-${customer.id}`">
            <!-- Main row -->
            <tr
              class="border-b hover:bg-muted/50 cursor-pointer"
              @click="toggleRow(customer)"
            >
              <!-- Expand toggle -->
              <td class="p-2 align-middle text-center">
                <ChevronDown v-if="isExpanded(customer)" class="h-4 w-4 text-muted-foreground" />
                <ChevronRight v-else class="h-4 w-4 text-muted-foreground" />
              </td>

              <!-- Type Badge -->
              <td class="p-4 align-middle">
                <Badge
                  v-if="customer.type === 'individual'"
                  class="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100"
                >
                  <User class="h-3 w-3 mr-1" />
                  Individual
                </Badge>
                <Badge
                  v-else
                  class="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100"
                >
                  <Building2 class="h-3 w-3 mr-1" />
                  Business
                </Badge>
              </td>

              <!-- Name -->
              <td class="p-4 align-middle">
                <div>
                  <div class="font-medium">{{ customer.name }}</div>
                  <!-- Show contact info for business customers -->
                  <div v-if="customer.type === 'business' && customer.contactName" class="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <User class="h-3 w-3" />
                    <span>{{ customer.contactName }}</span>
                    <span v-if="customer.contactEmail" class="text-muted-foreground">{{ customer.contactEmail }}</span>
                  </div>
                </div>
              </td>

              <!-- Status -->
              <td class="p-4 align-middle text-center">
                <KycStatusDisplay
                  :kyc-status="getOverallStatus(customer)"
                  :raw-kyc-status="customer.kyc_status"
                  :tos-status="customer.tos_status"
                  :customer-id="customer.id"
                  :bridge-customer-id="customer.bridge_customer_id"
                  :is-super-user="true"
                  :label="customer.type === 'business' ? 'KYB' : 'KYC'"
                  @synced="emit('customer-updated')"
                />
              </td>

              <!-- Created -->
              <td class="p-4 align-middle text-sm text-muted-foreground">
                {{ formatDate(customer.created_at) }}
              </td>

              <!-- Actions -->
              <td class="p-2 align-middle text-center" @click.stop>
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="h-7 w-7">
                      <MoreHorizontal class="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      class="text-destructive focus:text-destructive"
                      @click="requestUnenroll(customer)"
                    >
                      Unenroll
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>

            <!-- Expanded details row -->
            <tr v-if="isExpanded(customer)" class="bg-muted/30">
              <td colspan="6" class="p-4">
                <div class="pl-6 space-y-4 text-sm">
                  <!-- Customer Info Section -->
                  <div>
                    <div class="flex items-center gap-2 mb-2 font-medium">
                      <Building2 v-if="customer.type === 'business'" class="h-4 w-4 text-muted-foreground" />
                      <User v-else class="h-4 w-4 text-muted-foreground" />
                      <span>{{ customer.type === 'business' ? 'Business' : 'Individual' }}</span>
                    </div>
                    <div class="flex items-center gap-3 ml-6 text-xs">
                      <!-- Contact Email (business only) -->
                      <template v-if="customer.type === 'business'">
                        <span class="text-muted-foreground">Contact:</span>
                        <span>{{ customer.contactEmail || '—' }}</span>
                        <button
                          @click.stop="changeEmailTarget = customer"
                          class="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                        >
                          <Pencil class="h-3 w-3" />
                        </button>
                        <span class="text-muted-foreground/40">·</span>
                      </template>

                      <!-- Bridge ID -->
                      <span class="text-muted-foreground">Bridge ID:</span>
                      <button
                        v-if="customer.bridge_customer_id"
                        @click.stop="copyToClipboard(customer.bridge_customer_id!)"
                        class="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 font-mono"
                      >
                        <span>{{ customer.bridge_customer_id }}</span>
                        <Copy class="h-3 w-3 text-muted-foreground" />
                      </button>
                      <span v-else class="text-muted-foreground italic">Not assigned</span>
                      <span class="text-muted-foreground/40">·</span>

                      <!-- ToS Status -->
                      <span class="text-muted-foreground">ToS:</span>
                      <a v-if="customer.link?.tos_url" :href="customer.link.tos_url" target="_blank" rel="noopener noreferrer" @click.stop>
                        <Badge :class="customer.tos_status === 'approved' ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-muted text-muted-foreground hover:bg-muted/70'" class="cursor-pointer transition-colors">{{ customer.tos_status || 'pending' }}</Badge>
                      </a>
                      <Badge v-else :class="customer.tos_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'">{{ customer.tos_status || 'pending' }}</Badge>
                      <span class="text-muted-foreground/40">·</span>

                      <!-- KYC/KYB Status -->
                      <span class="text-muted-foreground">{{ customer.type === 'business' ? 'KYB' : 'KYC' }}:</span>
                      <a v-if="customer.link?.kyc_url" :href="customer.link.kyc_url" target="_blank" rel="noopener noreferrer" @click.stop>
                        <Badge
                          :class="{
                            'bg-green-100 text-green-700 hover:bg-green-200': customer.kyc_status === 'approved',
                            'bg-red-100 text-red-700 hover:bg-red-200': ['rejected', 'paused', 'offboarded'].includes(customer.kyc_status),
                            'bg-amber-100 text-amber-700 hover:bg-amber-200': customer.kyc_status === 'awaiting_ubo',
                            'bg-muted text-muted-foreground hover:bg-muted/70': !['approved', 'rejected', 'paused', 'offboarded', 'awaiting_ubo'].includes(customer.kyc_status)
                          }"
                          class="cursor-pointer transition-colors"
                        >{{ customer.kyc_status || 'not_started' }}</Badge>
                      </a>
                      <Badge
                        v-else
                        :class="{
                          'bg-green-100 text-green-700': customer.kyc_status === 'approved',
                          'bg-red-100 text-red-700': ['rejected', 'paused', 'offboarded'].includes(customer.kyc_status),
                          'bg-amber-100 text-amber-700': customer.kyc_status === 'awaiting_ubo',
                          'bg-muted text-muted-foreground': !['approved', 'rejected', 'paused', 'offboarded', 'awaiting_ubo'].includes(customer.kyc_status)
                        }"
                      >{{ customer.kyc_status || 'not_started' }}</Badge>
                    </div>
                  </div>

                  <!-- Accounts Section -->
                  <div v-if="getAccountData(customer)">
                    <!-- Loading State -->
                    <div v-if="getAccountData(customer)!.loading" class="flex items-center gap-2 text-muted-foreground py-4">
                      <div class="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                      <span>Loading account data...</span>
                    </div>

                    <!-- Error State -->
                    <div v-else-if="getAccountData(customer)!.error" class="flex items-center gap-2 text-destructive py-4">
                      <AlertCircle class="h-4 w-4" />
                      <span>{{ getAccountData(customer)!.error }}</span>
                    </div>

                    <!-- Account Data -->
                    <div v-else class="space-y-4">
                      <!-- Virtual Accounts Section -->
                      <div>
                        <div class="flex items-center gap-2 mb-2 font-medium">
                          <Building class="h-4 w-4 text-primary" />
                          <span>Virtual Accounts (Fiat → Crypto)</span>
                          <Badge variant="secondary" class="ml-2">{{ getAccountData(customer)!.virtualAccounts?.length || 0 }}</Badge>
                        </div>
                        <div v-if="!getAccountData(customer)!.virtualAccounts?.length" class="ml-6">
                          <div v-if="showingCreateVA !== `${customer.type}-${customer.id}`">
                            <div v-if="customer.type === 'business' && customer.kyc_status === 'approved'" class="flex items-center gap-2">
                              <span class="text-xs text-muted-foreground">No virtual accounts</span>
                              <Button
                                variant="link"
                                size="sm"
                                @click.stop="showingCreateVA = `${customer.type}-${customer.id}`"
                                class="h-auto p-0 text-xs"
                              >
                                Create
                              </Button>
                            </div>
                            <div v-else class="text-xs text-muted-foreground">
                              No virtual accounts{{ customer.type === 'business' ? ' (KYB must be approved first)' : '' }}
                            </div>
                          </div>
                          <CreateVirtualAccountForm
                            v-else
                            :customer-id="customer.id"
                            @success="handleVASuccess(customer)"
                            @cancel="showingCreateVA = null"
                          />
                        </div>
                        <div class="space-y-1 ml-6">
                          <div
                            v-for="va in getAccountData(customer)!.virtualAccounts"
                            :key="va.id"
                            class="flex items-center gap-2 text-xs"
                          >
                            <span class="font-mono text-muted-foreground">{{ va.id.slice(0, 8) }}…</span>
                            <Badge :class="va.status === 'activated' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'" class="text-xs">{{ va.status }}</Badge>
                            <span class="text-muted-foreground">{{ va.source_deposit_instructions?.bank_name || 'N/A' }} → {{ va.destination?.currency?.toUpperCase() || 'N/A' }} ({{ va.destination?.payment_rail || 'N/A' }})</span>
                          </div>
                        </div>
                      </div>

                      <!-- External Accounts Section -->
                      <div>
                        <div class="flex items-center gap-2 mb-2 font-medium">
                          <CreditCard class="h-4 w-4 text-purple-600" />
                          <span>External Bank Accounts</span>
                          <Badge variant="secondary" class="ml-2">{{ getAccountData(customer)!.externalAccounts?.length || 0 }}</Badge>
                        </div>
                        <div v-if="!getAccountData(customer)!.externalAccounts?.length" class="ml-6">
                          <div v-if="!isShowingConnectForm(customer)">
                            <div v-if="customer.kyc_status === 'approved'" class="flex items-center gap-2">
                              <span class="text-xs text-muted-foreground">No external accounts</span>
                              <Button
                                variant="link"
                                size="sm"
                                @click.stop="showConnectForm(customer)"
                                class="h-auto p-0 text-xs"
                              >
                                Connect
                              </Button>
                            </div>
                            <div v-else class="text-xs text-muted-foreground">
                              No external accounts ({{ customer.type === 'business' ? 'KYB' : 'KYC' }} must be approved first)
                            </div>
                          </div>
                          <ConnectExternalAccountForm
                            v-else
                            :customer-id="customer.id"
                            @success="handleConnectSuccess(customer)"
                            @cancel="hideConnectForm"
                          />
                        </div>
                        <div class="space-y-1 ml-6">
                          <div
                            v-for="ea in getAccountData(customer)!.externalAccounts"
                            :key="ea.id"
                            class="flex items-center gap-2 text-xs"
                          >
                            <span class="font-mono text-muted-foreground">{{ ea.id.slice(0, 8) }}…</span>
                            <Badge :class="ea.active ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'" class="text-xs">{{ ea.active ? 'Active' : 'Inactive' }}</Badge>
                            <Badge v-if="ea.beneficiary_address_valid" class="bg-green-100 text-green-700 text-xs">Validated</Badge>
                            <span class="text-muted-foreground">{{ ea.bank_name || 'N/A' }} · {{ ea.account_owner_name || 'N/A' }} · {{ ea.account_type?.toUpperCase() || 'N/A' }} · {{ ea.currency?.toUpperCase() || 'N/A' }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Wallets Section -->
                      <div>
                        <div class="flex items-center gap-2 mb-2 font-medium">
                          <Wallet class="h-4 w-4 text-amber-600" />
                          <span>Bridge Wallets</span>
                          <Badge variant="secondary" class="ml-2">{{ getAccountData(customer)!.wallets?.length || 0 }}</Badge>
                        </div>
                        <div v-if="getAccountData(customer)!.wallets?.length && getAccountData(customer)!.ledgerBalance !== null" class="text-xs text-muted-foreground ml-6 mb-1">
                          Balance: {{ getAccountData(customer)!.ledgerBalance }}
                        </div>
                        <div v-if="!getAccountData(customer)!.wallets?.length" class="text-xs text-muted-foreground ml-6">
                          No wallets
                        </div>
                        <div class="space-y-1 ml-6">
                          <div
                            v-for="wallet in getAccountData(customer)!.wallets"
                            :key="wallet.id"
                            class="flex items-center gap-2 text-xs"
                          >
                            <Badge :class="wallet.chain === 'base' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'" class="text-xs">{{ wallet.chain }}</Badge>
                            <button
                              @click.stop="copyToClipboard(wallet.address)"
                              class="flex items-center gap-1 hover:bg-muted rounded px-1 font-mono text-muted-foreground"
                            >
                              <span>{{ wallet.address.slice(0, 12) }}…{{ wallet.address.slice(-6) }}</span>
                              <Copy class="h-3 w-3 flex-shrink-0" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>

  <ChangeContactEmailDialog
    v-if="changeEmailTarget"
    :open="!!changeEmailTarget"
    :customer-id="changeEmailTarget.id"
    :current-email="changeEmailTarget.contactEmail"
    @update:open="(v) => { if (!v) changeEmailTarget = null }"
    @updated="handleEmailUpdated(changeEmailTarget!, $event)"
  />

  <DialogConfirm
    ref="confirmUnenrollDialog"
    title="Unenroll customer"
    :description="pendingUnenroll ? `Remove ${pendingUnenroll.name} from Bridge? This deletes all local records. Cannot be undone if transfers exist.` : ''"
    confirm-text="Unenroll"
    variant="error"
    @confirm="confirmUnenroll"
  />
</template>
